/**
 * /wireline-slickline/wells/:id — one well
 * (GET / PATCH / DELETE /wireline-slickline/wells/:id, fieldiq-api#1336).
 *
 * Panels stack full width: details on top, the edit form below them when
 * editing (never a modal).
 *
 * Edit is WL_WELL_WRITE (admin, office_staff, supervisor); delete is WL_ADMIN
 * (admin, office_staff) and asks in-app first. Any other role reads. A
 * refusal that still arrives is shown as a refusal in the form, never as a
 * green toast; a 409 duplicate_well names the existing well and links to it.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, Droplet, MapPin, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useDeleteWlWell, useUpdateWlWell, useWlCustomers, useWlMeta, useWlWell } from '../../hooks/useWirelineSlickline';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canAdminWl,
  canWriteWlWells,
  wlCatalogLabel,
  duplicateWlWell,
  fmtWlNumber,
  wlWellIdLabel,
  wlWellIdOf,
  wlWithUnit,
  wlRefusal,
} from '../../lib/wireline-slickline';
import { useBackToList } from '../downhole/recordPage';
import { WellForm } from './WellForm';
import { isNotFound, WL_BAND_SECONDARY, WL_EYEBROW, WL_PRIMARY, WL_SECONDARY, WL_SOUR_SKIN, WlBadge, WlDialog, WlEmpty, WlFormError, WlLoading, WlPanel, WlSpec } from './WlBits';

export default function WlWellDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/wireline-slickline/wells');
  const { user } = useAuth();
  const query = useWlWell(id);
  const catalog = useWlMeta().data?.data ?? null;
  const canEdit = canWriteWlWells(user?.role);
  const canDelete = canAdminWl(user?.role);
  const customers = useWlCustomers(canEdit);
  const update = useUpdateWlWell();
  const del = useDeleteWlWell();
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [duplicateOf, setDuplicateOf] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // The duplicate link opens another well on this same route: start it closed.
  useEffect(() => {
    setEditing(false);
    setEditError(null);
    setDuplicateOf(null);
    setDeleting(false);
  }, [id]);

  const well = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const fluid = well ? well.fluidTypeLabel ?? wlCatalogLabel(catalog?.fluidTypes, well.fluidType) : '—';
  const idLabel = wlWellIdLabel(meta);

  return (
    <div data-testid="wl-well-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title={well?.wellName || (query.isLoading ? 'Well' : '—')}
        onBack={back}
        backLabel="Back"
        actions={
          well && (canEdit || canDelete) ? (
            <div className="flex flex-wrap gap-2">
              {canEdit && (
                <button
                  type="button"
                  className="fi-action fi-action--standard fi-action--primary"
                  onClick={() => {
                    setEditError(null);
                    setDuplicateOf(null);
                    setEditing(true);
                  }}
                  disabled={editing}
                >
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit well
                </button>
              )}
              {canDelete && (
                <button type="button" className={WL_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Droplet className="size-4" aria-hidden="true" />, label: 'Fluid', tone: 'neutral', loading: query.isLoading, value: fluid },
          {
            icon: <AlertTriangle className="size-4" aria-hidden="true" />,
            label: 'Service',
            tone: well?.sour ? 'accent' : 'neutral',
            loading: query.isLoading,
            value: well ? (well.sour ? 'Sour' : 'Sweet') : '—',
          },
          { icon: <MapPin className="size-4" aria-hidden="true" />, label: 'Region', tone: 'neutral', loading: query.isLoading, value: well?.provinceState || '—' },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WlLoading label="Loading well" />
          ) : notFound || (!failure && !well) ? (
            <WlPanel eyebrow="Well" title="Well">
              <WlEmpty>This well was not found. It may have been deleted.</WlEmpty>
            </WlPanel>
          ) : failure || !well ? (
            <WlPanel eyebrow="Well" title="Well">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this well" />
            </WlPanel>
          ) : (
            <>
              <WlPanel eyebrow="Well" title="Details" testId="wl-well-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <WlSpec label="Customer" value={well.customerName || '—'} />
                  <WlSpec label={idLabel} value={wlWellIdOf(well, meta) || '—'} mono />
                  {/* The other country's identifier, only when this well holds one. */}
                  {idLabel === 'UWI' && well.apiNumber && <WlSpec label="API number" value={well.apiNumber} mono />}
                  {idLabel === 'API number' && well.uwi && <WlSpec label="UWI" value={well.uwi} mono />}
                  <WlSpec label="Licence number" value={well.licenceNumber || '—'} mono />
                  <WlSpec label="Surface location" value={well.surfaceLocation || '—'} />
                  <WlSpec label="Province / state" value={well.provinceName || well.provinceState || '—'} />
                  <WlSpec
                    label="Coordinates"
                    value={well.latitude != null && well.longitude != null ? `${fmtWlNumber(well.latitude)}, ${fmtWlNumber(well.longitude)}` : '—'}
                    mono
                  />
                  <WlSpec label="Field" value={well.fieldName || '—'} />
                  <WlSpec label="Pool" value={well.poolName || '—'} />
                  <WlSpec label="Fluid" value={fluid} />
                  <WlSpec label="Service" value={well.sour ? <WlBadge skin={WL_SOUR_SKIN} value="sour" /> : 'Sweet'} />
                  <WlSpec label="Expected H2S" value={wlWithUnit(well.expectedH2sPpm, 'ppm')} mono />
                </dl>
                {well.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{well.notes}</p>}
              </WlPanel>

              {editing && canEdit && (
                <WlPanel eyebrow="Edit" title="Edit well" testId="wl-well-edit">
                  <WellForm
                    key={well.id}
                    well={well}
                    catalog={catalog}
                    meta={meta}
                    customers={customers.data ?? []}
                    pending={update.isPending}
                    error={editError}
                    duplicateOf={duplicateOf}
                    submitLabel="Save well"
                    onCancel={() => setEditing(false)}
                    onSubmit={(body) => {
                      setEditError(null);
                      setDuplicateOf(null);
                      update.mutate(
                        { id: well.id, body },
                        {
                          onSuccess: () => {
                            toast.success('Well saved');
                            setEditing(false);
                          },
                          onError: (err) => {
                            const dup = duplicateWlWell(err);
                            setEditError(dup ? dup.message : wlRefusal(err, 'The well could not be saved.'));
                            setDuplicateOf(dup?.existingWellId ?? null);
                          },
                        },
                      );
                    }}
                  />
                </WlPanel>
              )}
            </>
          )}
        </div>
      </section>

      {well && canDelete && (
        <WlDialog
          open={deleting}
          title="Delete well"
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={WL_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                Keep well
              </button>
              <button
                type="button"
                className={WL_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(well.id, {
                    onSuccess: () => {
                      toast.success('Well deleted');
                      navigate('/wireline-slickline/wells', { replace: true });
                    },
                    onError: (err) => setDeleteError(wlRefusal(err, 'The well could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete well'}
              </button>
            </>
          }
        >
          <WlFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">
            Removes {well.wellName} from the register.
            {wlWellIdOf(well, meta) ? ` Its ${idLabel} is freed for another well.` : ''}
          </p>
        </WlDialog>
      )}
    </div>
  );
}
